import { useState, useEffect } from 'react';
import { 
  saveQuizResponses, 
  loadQuizResponses, 
  clearQuizData, 
  hasCompletedQuiz 
} from '../utils/quizStorage';

/**
 * Custom hook for managing quiz state with localStorage persistence
 * Handles loading saved responses, saving new responses, and managing completion state
 */
export const useQuizState = (questions) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [hasLoadedSavedData, setHasLoadedSavedData] = useState(false);

  // Load saved quiz data on mount
  useEffect(() => {
    const savedResponses = loadQuizResponses();
    const wasCompleted = hasCompletedQuiz();
    
    if (savedResponses) {
      setAnswers(savedResponses);
      setHasLoadedSavedData(true);
      
      // If quiz was previously completed, show completion state
      if (wasCompleted && Object.keys(savedResponses).length === questions.length) {
        setIsComplete(true);
      }
    }
  }, [questions.length]);

  // Save answers to localStorage whenever they change
  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      saveQuizResponses(answers);
    }
  }, [answers]);

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      // Quiz completed - save final state
      const finalAnswers = { ...answers };
      saveQuizResponses(finalAnswers);
      setIsComplete(true);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const restartQuiz = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setIsComplete(false);
    setHasLoadedSavedData(false);
    clearQuizData();
  };

  const resumeFromLastQuestion = () => {
    // Find the last answered question
    const answeredQuestions = Object.keys(answers).length;
    if (answeredQuestions > 0 && answeredQuestions < questions.length) {
      setCurrentQuestion(answeredQuestions);
      setIsComplete(false);
    }
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const totalAnswered = Object.keys(answers).length;
  const canResume = hasLoadedSavedData && totalAnswered > 0 && totalAnswered < questions.length;

  return {
    currentQuestion,
    answers,
    isComplete,
    hasLoadedSavedData,
    canResume,
    totalAnswered,
    progress,
    handleAnswer,
    nextQuestion,
    prevQuestion,
    restartQuiz,
    resumeFromLastQuestion,
  };
};